import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskProductAddModalConfig as KioskProductAddModalContentConfig } from '@front-platform/content-builder';
import { kioskProductAddModalResolveConfig } from '@front-platform/content-builder';
import { AsyncPipe } from '@angular/common';
import { Component, HostListener, inject, input, OnDestroy, computed } from '@angular/core';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { ICartItem } from '@core/models/cart.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CartService } from '@core/services/cart/cart.service';
import { CloseModalOnBrowserBack } from '@core/services/modal-back-history/modal-close-on-back.decorator';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import {
  KioskProductAddShellAddToCartPayload,
  KioskProductAddShellComponent,
} from '@shared/pages/kiosk/kiosk-product-add-shell/kiosk-product-add-shell.component';
import { map, Observable, take } from 'rxjs';

/** Content Builder grouped field `theme_colors` — inner keys stay camelCase in CMS. */
export interface KioskProductAddModalThemeColors {
  backgroundColor?: IContentBuilderFieldColor;
  secondaryColor?: IContentBuilderFieldColor;
  textColor?: IContentBuilderFieldColor;
  buttonBackgroundColor?: IContentBuilderFieldColor;
  buttonTextColor?: IContentBuilderFieldColor;
  unavailableButtonBackground?: IContentBuilderFieldColor;
  unavailableButtonText?: IContentBuilderFieldColor;
  cardColor?: IContentBuilderFieldColor;
  compositionBackgroundColor?: IContentBuilderFieldColor;
}

export type KioskProductAddModalConfig = ModalConfig<{
  handle: typeof KioskProductAddModalComponent.handle;
  inputs: {
    themeColors?: KioskProductAddModalThemeColors;
    /** Resolved shop and product for kiosk add flow (no content store product slice). */
    productId?: string;
    shopId?: string;
  };
  outputs: { type: 'dismissed' } | { type: 'addedToCart' };
}>;
export interface KioskProductAddModalRuntimeConfig {
  'product_id'?: string;
  'shop_id'?: string;
}

export type KioskProductAddModalConfigInput = Partial<
  Omit<KioskProductAddModalContentConfig, keyof KioskProductAddModalRuntimeConfig> &
    KioskProductAddModalRuntimeConfig
>;
export type KioskProductAddModalResolvedConfig = Omit<
  KioskProductAddModalContentConfig,
  keyof KioskProductAddModalRuntimeConfig
> &
  Partial<KioskProductAddModalRuntimeConfig>;
export interface KioskProductAddModalContract extends ConfigComponentContract<
  KioskProductAddModalConfigInput,
  KioskProductAddModalResolvedConfig
> {}

@CloseModalOnBrowserBack()
@Component({
  selector: 'app-kiosk-product-add-modal',
  templateUrl: './kiosk-product-add-modal.component.html',
  standalone: true,
  imports: [AsyncPipe, KioskProductAddShellComponent],
})
export class KioskProductAddModalComponent
  extends ModalScrollBlockBase
  implements OnDestroy, GetModalInterface<KioskProductAddModalConfig>, KioskProductAddModalContract
{
  readonly config = input<KioskProductAddModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskProductAddModalContentConfig,
    KioskProductAddModalRuntimeConfig
  >(kioskProductAddModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'kiosk-product-add-modal' as const;

  private readonly modalSelector = inject(ModalStoreSelector);
  private readonly modalsService = inject(ModalsService);
  private readonly cartService = inject(CartService);

  index$: Observable<number> = this.modalSelector
    .selectModal(KioskProductAddModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  readonly themeColors = computed(
    () => this.resolvedConfig()['theme_colors'] as unknown as KioskProductAddModalThemeColors,
  );
  readonly productId = computed(() => this.resolvedConfig()['product_id'] as unknown as string);
  readonly shopId = computed(() => this.resolvedConfig()['shop_id'] as unknown as string);

  constructor() {
    super();
  }

  onShellAddToCart(payload: KioskProductAddShellAddToCartPayload): void {
    if (!payload.validity.isValid) {
      return;
    }
    const newCartItem = payload.cartDraft as unknown as Omit<ICartItem, 'id'>;
    this.cartService
      .addOrUpdateCartItem$({
        newCartItem,
        quantity: payload.quantity,
        product: payload.product,
      })
      .pipe(take(1))
      .subscribe(() => this.closeAsAdded());
  }

  closeAsAdded(): void {
    this.modalsService.emitEvent({
      handle: KioskProductAddModalComponent.handle,
      data: { type: 'addedToCart' },
    });
    this.modalsService.close(KioskProductAddModalComponent.handle);
  }

  closeAsDismissed(): void {
    this.modalsService.emitEvent({
      handle: KioskProductAddModalComponent.handle,
      data: { type: 'dismissed' },
    });
    this.modalsService.close(KioskProductAddModalComponent.handle);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.productId() && this.shopId()) {
      event.preventDefault();
      this.closeAsDismissed();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskProductAddModalComponent, { 'keyAliases': {} });
